// src/components/DashboardComponents/ExportMenu.tsx
import { useState, forwardRef, useImperativeHandle } from "react";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { Expense } from "../../types/types";
import type { useToast } from "../ToastProvider/ToastProvider";

export interface IncomeSource {
  id: string;
  name: string;
  amount1?: number;
  amount2?: number;
  amount3?: number;
  amount4?: number;
  frequency: "weekly" | "biweekly" | "monthly" | "one-time";
  color?: string;
}

interface ExportMenuProps {
  expenses: Expense[];
  waveAmounts: number[]; // [wave1, wave2, wave3, wave4]
  expenseListFrequency: "weekly" | "biweekly" | "monthly";
  incomeSources?: IncomeSource[];
  waveCount: number;
  waveTotals: number[];
  waveCounts: number[];
  waveBalances: number[];
  t: any;
  language: "en" | "es";
  toast: ReturnType<typeof useToast>;
  getWaveLabel: (wave: number, frequency: string, t: any) => string;
  API_URL?: string;
}

export interface ExportMenuRef {
  exportToPDF: () => void;
  exportToCSV: () => void;
  exportToJSON: () => void;
  exportToExcel: () => void;
}

const ExportMenu = forwardRef<ExportMenuRef, ExportMenuProps>(({
  expenses,
  waveAmounts,
  expenseListFrequency,
  incomeSources = [],
  waveCount,
  waveTotals,
  waveCounts,
  waveBalances,
  t,
  language: _language,
  toast,
  getWaveLabel,
}, ref) => {
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const totalWaveBudget = waveAmounts.slice(0, waveCount).reduce((s, a) => s + (a ?? 0), 0);

  const generatePDFContent = () => {
    const incomeHtml = incomeSources.length
      ? `<h2>Income Sources</h2>
         <table>
           <tr><th>Source</th><th>Frequency</th><th>Wave amounts</th><th>Monthly Est.</th></tr>
           ${incomeSources.map(src => {
             const waves = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
               .slice(0, src.frequency === "weekly" ? 4 : src.frequency === "biweekly" ? 2 : 1)
               .map((a, i) => `W${i+1}: $${a.toFixed(2)}`)
               .join(" / ");
             const monthly = src.frequency === "weekly"
               ? (src.amount1 ?? 0) + (src.amount2 ?? 0) + (src.amount3 ?? 0) + (src.amount4 ?? 0)
               : src.frequency === "biweekly"
                 ? (src.amount1 ?? 0) + (src.amount2 ?? 0)
                 : (src.amount1 ?? 0);
             return `<tr><td>${src.name}</td><td>${src.frequency}</td><td>${waves}</td><td>$${monthly.toFixed(2)}</td></tr>`;
           }).join("")}
         </table>`
      : "";

    return `<!doctype html><html><head><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;margin:40px;color:#333}h1{color:#315EFF;border-bottom:3px solid #315EFF;padding-bottom:10px}table{border-collapse:collapse;width:100%;margin:20px 0}th,td{border:1px solid #ddd;padding:12px 8px;text-align:left}th{background:#315EFF;color:#fff}
      .summary{background:#f0f4ff;padding:12px;border-radius:8px;border:2px solid #315EFF;margin-bottom:20px}
    </style></head><body>
      <h1>Budget Tracker Report</h1>
      <p><strong>Export Date:</strong> ${new Date().toLocaleString()}</p>
      <div class="summary">
        <div>Total Expenses: ${expenses.length}</div>
        <div>Paid: ${expenses.filter(e => e.isPaid).length}</div>
        <div>Unpaid: ${expenses.filter(e => !e.isPaid).length}</div>
        <div>Total Amount: $${expenses.reduce((s,e) => s + e.price, 0).toFixed(2)}</div>
        ${waveAmounts.slice(0, waveCount).map((amt,i) => `<div>${getWaveLabel(i+1, expenseListFrequency, t)} Budget: $${(amt ?? 0).toFixed(2)}</div>`).join("")}
        <div>Total Wave Budget: $${totalWaveBudget.toFixed(2)}</div>
        <div>Frequency: ${expenseListFrequency}</div>
      </div>
      ${incomeHtml}
      <h2>Wave Summary</h2>
      <table><tr><th>Wave</th><th>Budget</th><th>Expenses</th><th>Count</th><th>Balance</th></tr>
      ${Array.from({length: waveCount}, (_,i) => `<tr><td>${getWaveLabel(i+1, expenseListFrequency, t)}</td><td>$${(waveAmounts[i] ?? 0).toFixed(2)}</td><td>$${(waveTotals[i] ?? 0).toFixed(2)}</td><td>${waveCounts[i]}</td><td>$${(waveBalances[i] ?? 0).toFixed(2)}</td></tr>`).join("")}
      </table>
      <h2>All Expenses</h2>
      <table><tr><th>Name</th><th>Amount</th><th>Due Date</th><th>Category</th><th>Wave</th><th>Status</th><th>Payment Type</th></tr>
      ${expenses.map(exp => `<tr><td>${exp.name}</td><td>$${exp.price.toFixed(2)}</td><td>${exp.dueDate}</td><td>${exp.category}</td><td>${(exp.category === "Wave" || exp.category === "Paycheck") ? getWaveLabel(exp.wave!, expenseListFrequency, t) : "N/A"}</td><td>${exp.isPaid ? "Paid":"Unpaid"}</td><td>${exp.paymentType ?? "N/A"}</td></tr>`).join("")}
      </table>
    </body></html>`;
  };

  const exportToCSV = () => {
    const headers = ["Expense Name","Amount","Due Date","Category","Wave/Week","Status","Payment Type","Payment URL"];
    const rows = expenses.map(exp => [
      exp.name,
      exp.price.toFixed(2),
      exp.dueDate,
      exp.category,
      (exp.category === "Wave" || exp.category === "Paycheck") ? (exp.wave ?? "N/A") : "N/A",
      exp.isPaid ? "Paid" : "Unpaid",
      exp.paymentType ?? "N/A",
      exp.paymentUrl ?? "N/A",
    ]);
    // also include income sources header block at top
    const incomeBlock = incomeSources.length
      ? ["Income Sources:", ...incomeSources.map(s => `${s.name} | ${s.frequency} | ${[s.amount1,s.amount2,s.amount3,s.amount4].filter(a => a != null).join(" / ")} | MonthlyEst:${(
        s.frequency === "weekly" ? (s.amount1 ?? 0) + (s.amount2 ?? 0) + (s.amount3 ?? 0) + (s.amount4 ?? 0)
        : s.frequency === "biweekly" ? (s.amount1 ?? 0) + (s.amount2 ?? 0)
        : (s.amount1 ?? 0)
      ).toFixed(2)}`)]
      : [];
    const csv = [ ...incomeBlock, headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(",")) ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `budget-tracker-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    setExportAnchorEl(null);
    toast.success(t.exportMenu.dataExportedCSV);
  };

  const exportToJSON = () => {
    const data = {
      exportDate: new Date().toISOString(),
      settings: {
        waveAmounts: waveAmounts.slice(0, waveCount),
        frequency: expenseListFrequency,
        totalWaveBudget,
      },
      incomeSources,
      summary: {
        totalExpenses: expenses.length,
        paidExpenses: expenses.filter(e => e.isPaid).length,
        unpaidExpenses: expenses.filter(e => !e.isPaid).length,
        totalAmount: expenses.reduce((s,e) => s + e.price, 0),
        waveDetails: Array.from({length: waveCount}, (_,i) => ({
          wave: i+1,
          label: getWaveLabel(i+1, expenseListFrequency, t),
          budget: waveAmounts[i] ?? 0,
          total: waveTotals[i] ?? 0,
          count: waveCounts[i],
          balance: waveBalances[i] ?? 0
        }))
      },
      expenses: expenses.map(exp => ({
        name: exp.name,
        amount: exp.price,
        dueDate: exp.dueDate,
        category: exp.category,
        wave: exp.wave,
        isPaid: exp.isPaid,
        paymentType: exp.paymentType,
        paymentUrl: exp.paymentUrl,
      }))
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `budget-tracker-${new Date().toISOString().split("T")[0]}.json`;
    a.click(); URL.revokeObjectURL(url);
    setExportAnchorEl(null);
    toast.success(t.exportMenu.dataExportedJSON);
  };

  const exportToExcel = () => {
    const html = generatePDFContent();
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `budget-tracker-${new Date().toISOString().split("T")[0]}.xls`;
    a.click(); URL.revokeObjectURL(url);
    setExportAnchorEl(null);
    toast.success(t.exportMenu.dataExportedExcel);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toISOString().split("T")[0];

    // Header bar
    doc.setFillColor(49, 94, 255);
    doc.rect(0, 0, doc.internal.pageSize.width, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16).setFont("helvetica", "bold");
    doc.text("WaveBudget Tracker — Budget Report", 14, 18);

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text(`Exported: ${new Date().toLocaleString()}   Frequency: ${expenseListFrequency}   Total Budget: $${totalWaveBudget.toFixed(2)}`, 14, 36);

    // Wave summary table
    doc.setFontSize(11).setFont("helvetica", "bold");
    doc.text("Wave Summary", 14, 46);
    autoTable(doc, {
      startY: 50,
      head: [["Wave", "Budget", "Expenses", "Count", "Balance"]],
      body: Array.from({ length: waveCount }, (_, i) => [
        getWaveLabel(i + 1, expenseListFrequency, t),
        `$${(waveAmounts[i] ?? 0).toFixed(2)}`,
        `$${(waveTotals[i] ?? 0).toFixed(2)}`,
        String(waveCounts[i] ?? 0),
        `$${(waveBalances[i] ?? 0).toFixed(2)}`,
      ]),
      headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { fontSize: 9, cellPadding: 3 },
    });

    // Income sources table (if any)
    if (incomeSources.length > 0) {
      const afterWave = (doc as any).lastAutoTable.finalY + 8;
      doc.setFontSize(11).setFont("helvetica", "bold");
      doc.text("Income Sources", 14, afterWave);
      autoTable(doc, {
        startY: afterWave + 4,
        head: [["Source", "Frequency", "Wave Amounts", "Monthly Est."]],
        body: incomeSources.map((src) => {
          const count = src.frequency === "weekly" ? 4 : src.frequency === "biweekly" ? 2 : 1;
          const amounts = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
            .slice(0, count).map((a, i) => `W${i + 1}: $${a.toFixed(2)}`).join(" / ");
          const monthly = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
            .slice(0, count).reduce((s, a) => s + a, 0);
          return [src.name, src.frequency, amounts, `$${monthly.toFixed(2)}`];
        }),
        headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        styles: { fontSize: 9, cellPadding: 3 },
      });
    }

    // Expenses table
    const afterPrev = (doc as any).lastAutoTable.finalY + 8;
    doc.setFontSize(11).setFont("helvetica", "bold");
    doc.text("All Expenses", 14, afterPrev);
    autoTable(doc, {
      startY: afterPrev + 4,
      head: [["Name", "Amount", "Due Date", "Category", "Wave", "Status", "Payment"]],
      body: expenses.map((exp) => [
        exp.name,
        `$${exp.price.toFixed(2)}`,
        exp.dueDate ?? "—",
        exp.category,
        (exp.category === "Wave" || exp.category === "Paycheck") && exp.wave
          ? getWaveLabel(exp.wave, expenseListFrequency, t)
          : "N/A",
        exp.isPaid ? "Paid" : "Unpaid",
        exp.paymentType ?? "N/A",
      ]),
      headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 40 } },
    });

    doc.save(`budget-tracker-${dateStr}.pdf`);
    setExportAnchorEl(null);
    toast.success(t.exportMenu.dataExportedPDF);
  };

  // Expose export functions via ref
  useImperativeHandle(ref, () => ({
    exportToPDF,
    exportToCSV,
    exportToJSON,
    exportToExcel,
  }));

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExportAnchorEl(e.currentTarget); }}
        className="icon-btn"
        title={t.exportMenu.exportData}
        aria-label={t.exportMenu.exportData}
      >
        <Download size={24} />
      </button>

      {exportMenuOpen && (
        <div
          className="export-menu-dropdown"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            background: "var(--card-bg)",
            border: "1px solid var(--border-color)",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            minWidth: 150,
          }}
        >
          <button className="export-menu-item" onClick={exportToPDF} style={{ display: "block", width: "100%", padding: "0.75rem 1rem", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "var(--text-color)" }}>{t.exportMenu.exportAsPDF}</button>
          <button className="export-menu-item" onClick={exportToCSV} style={{ display: "block", width: "100%", padding: "0.75rem 1rem", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "var(--text-color)" }}>{t.exportMenu.exportAsCSV}</button>
          <button className="export-menu-item" onClick={exportToJSON} style={{ display: "block", width: "100%", padding: "0.75rem 1rem", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "var(--text-color)" }}>{t.exportMenu.exportAsJSON}</button>
          <button className="export-menu-item" onClick={exportToExcel} style={{ display: "block", width: "100%", padding: "0.75rem 1rem", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "var(--text-color)" }}>{t.exportMenu.exportAsExcel}</button>
        </div>
      )}
      {exportMenuOpen && (
        <div
          className="export-menu-backdrop"
          onClick={() => setExportAnchorEl(null)}
          style={{ position: "fixed", inset: 0, zIndex: 999 }}
        />
      )}
    </>
  );
});

ExportMenu.displayName = 'ExportMenu';

export default ExportMenu;
